import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const catPerView = () => (window.innerWidth < 640 ? 2 : window.innerWidth < 1024 ? 3 : 6);

// Endless right-to-left carousel that steps one tile every `speed` seconds and pauses on hover.
export function CategoryCarousel({ items, speed = 3, renderItem, perView = catPerView, testid = "category-carousel", label = "categories" }) {
  const n = items.length;
  const [per, setPer] = useState(perView);
  const [idx, setIdx] = useState(0);
  const [anim, setAnim] = useState(true);
  const [hover, setHover] = useState(false);
  const busy = useRef(false);
  const touch = useRef(null);
  const [edge, setEdge] = useState(0);
  const goRef = useRef(null);

  useEffect(() => { const f = () => setPer(perView()); window.addEventListener("resize", f); return () => window.removeEventListener("resize", f); }, [perView]);

  const go = useCallback((d) => {
    if (busy.current) return;
    busy.current = true;
    if (d < 0 && idx === 0) {
      setAnim(false); setIdx(n);
      requestAnimationFrame(() => requestAnimationFrame(() => { setAnim(true); setIdx(n - 1); }));
    } else { setAnim(true); setIdx((i) => i + d); }
    setTimeout(() => { busy.current = false; }, 520);
  }, [idx, n]);

  goRef.current = go;
  useEffect(() => {
    if (!edge || n <= per) return undefined;
    goRef.current(edge);
    const t = setInterval(() => goRef.current(edge), 750);
    return () => clearInterval(t);
  }, [edge, n, per]);

  const onMove = (e) => {
    if (e.pointerType && e.pointerType !== "mouse") return;
    const r = e.currentTarget.getBoundingClientRect();
    const f = (e.clientX - r.left) / r.width;
    const z = f < 0.25 ? -1 : f > 0.75 ? 1 : 0;
    if (z !== edge) setEdge(z);
  };

  useEffect(() => {
    if (hover || n <= per) return undefined;
    const t = setInterval(() => go(1), Math.max(1, speed) * 1000);
    return () => clearInterval(t);
  }, [hover, n, per, speed, go]);

  const onTouchStart = (e) => { touch.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }; setHover(true); };
  const onTouchEnd = (e) => {
    const t = touch.current; touch.current = null; setHover(false);
    if (!t || n <= per) return;
    const dx = e.changedTouches[0].clientX - t.x, dy = e.changedTouches[0].clientY - t.y;
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) go(dx < 0 ? 1 : -1);
  };
  const onEnd = () => { if (idx >= n) { setAnim(false); setIdx(idx - n); } };
  const loop = n > per;
  const list = loop ? [...items, ...items] : items;

  return (
    <div className="relative" onMouseEnter={() => setHover(true)} onMouseLeave={() => { setHover(false); setEdge(0); }} onPointerMove={onMove} data-testid={testid} data-paused={hover ? "yes" : "no"} data-edge={edge}>
      {loop && <Arrow side="left" onClick={() => go(-1)} testid={testid} label={label} />}
      <div className="overflow-hidden mx-9 sm:mx-11 py-1 touch-pan-y" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} onTouchCancel={() => { touch.current = null; setHover(false); }} data-testid={`${testid}-viewport`}>
        <div className={`flex ${loop ? "" : "justify-center"}`} onTransitionEnd={onEnd} data-testid={`${testid}-track`} data-index={idx % n}
          style={{ transform: loop ? `translateX(-${(idx * 100) / per}%)` : "none", transition: anim ? "transform 500ms ease" : "none" }}>
          {list.map((it, i) => (
            <div key={`${it.id}-${i}`} className="shrink-0 px-1.5 sm:px-2" style={{ width: `${100 / per}%` }} aria-hidden={i >= n ? "true" : undefined}>
              {renderItem(it, i >= n)}
            </div>
          ))}
        </div>
      </div>
      {loop && <Arrow side="right" onClick={() => go(1)} testid={testid} label={label} />}
      {loop && (
        <div className="lg:hidden mt-3 flex justify-center gap-1.5" data-testid={`${testid}-dots`}>
          {items.map((it, i) => (
            <button key={it.id} type="button" onClick={() => { setAnim(true); setIdx(i); }} aria-label={`Go to item ${i + 1}`} data-testid={`${testid}-dot-${i}`}
              className={`h-1.5 rounded-full transition-all duration-300 ${idx % n === i ? "w-5 bg-[#F5551E]" : "w-1.5 bg-slate-300 hover:bg-slate-400"}`} />
          ))}
        </div>
      )}
    </div>
  );
}

function Arrow({ side, onClick, testid, label }) {
  const Icon = side === "left" ? ChevronLeft : ChevronRight;
  return (
    <button type="button" onClick={onClick} aria-label={`${side === "left" ? "Previous" : "Next"} ${label}`} data-testid={`${testid}-${side}`}
      className={`absolute top-1/2 -translate-y-1/2 ${side === "left" ? "left-0" : "right-0"} z-10 h-8 w-8 sm:h-9 sm:w-9 rounded-full bg-white border border-slate-200 shadow-md text-[#12234A] flex items-center justify-center hover:bg-[#F5551E] hover:text-white hover:border-[#F5551E] transition-colors`}>
      <Icon size={18} />
    </button>
  );
}
